import { memo, Suspense, useMemo } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { AlertTriangleOutline, Menu2, Menu3 } from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import { Loader, Stack, Typography } from '@lyearn/ui';
import * as Editor from '@/components/Editor/PlainEditor/components/Editor';
import { AI_COLORS } from '@/components/LyearnAI/consts';
import { useGenerativeTaskContext } from '@/components/LyearnAI/context/generativeTaskContext';
import AIFeedback from '@/features/ai-feedback';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { TextTransformationEnum } from '@/types/schema';

import { useAIEditorContext } from '../../context/AIEditorContext';
import { AIBaseEditorProps, AIMessageInputProps } from '../../types';
import AIBaseEditor from '../AIBaseEditor';
import AISuggestionContainer from '../AIBaseEditor/components/AISuggestionContainer';
import { AI_MESSAGE_INPUT_EDITOR_OPTION_MAP, AI_MESSAGE_INPUT_EDITOR_OPTIONS } from './consts';

const nudgeBorderStyle = css`
  background:
    linear-gradient(var(--bg-secondary) 0 0) padding-box,
    linear-gradient(
        90deg,
        ${AI_COLORS.lavenderBlue} 0%,
        ${AI_COLORS.lilacPink} 67.71%,
        ${AI_COLORS.fuchsiaPink} 100%
      )
      border-box;
`;

const EDITOR_CLASSES: NonNullable<AIBaseEditorProps['classes']> = {
  root: 'rounded-8 bg-bg-secondary border border-transparent',
  toolbar: {
    root: 'h-0 w-0 overflow-hidden',
  },
};

const SuggestionComponent: React.FC = () => {
  const { t } = useTranslation(['AI']);

  return (
    <AISuggestionContainer
      bodyTypographyProps={{ variant: 'body-short-02', color: 'text-text-primary' }}
      className="border-t border-divider py-16 ps-20 pe-42"
      classes={{
        header: { root: 'mb-8', icon: 'h-16 w-16', text: 'ms-6' },
      }}
      headerData={{
        [TextTransformationEnum.Expand]: {
          label: t('AI:MakeLonger'),
          Icon: Menu3,
        },
        [TextTransformationEnum.Contract]: {
          label: t('AI:MakeShorter'),
          Icon: Menu2,
        },
      }}
      headerTypographyProps={{ variant: 'label-02' }}
      showHeader={true}
      showHeaderIcon={true}
    />
  );
};

const FeedbackComponent: React.FC = () => {
  const { isDone, generativeTaskId } = useGenerativeTaskContext();
  const { suggestedText } = useAIEditorContext();
  const { t } = useTranslation(['AI']);

  if (!generativeTaskId || !suggestedText || !isDone) {
    return null;
  }

  return (
    <Stack alignItems="center" className="mt-4 mb-16 h-24 px-20" justifyContent="between">
      <Stack alignItems="center" gapX={8}>
        <AlertTriangleOutline className="h-16 w-16 text-icon-placeholder" />
        <Typography color="text-text-placeholder" variant="body-short-03">
          {t('AI:AIResponsesCanBeInaccurate')}
        </Typography>
        <AIFeedback
          showOnlyIcon
          buttonSize="xs"
          className="!p-0 ms-4"
          entityId={generativeTaskId}
          gapX={2}
          motionDivClassname="flex-grow-0"
          position="top-end"
        />
      </Stack>
    </Stack>
  );
};

function AIMessageInput({
  disabled,
  value,
  onChange,
  error,
  placeholder,
  className,
  topic,
  onTopicError,
  textUseCase,
}: AIMessageInputProps) {
  const { [FeatureFlags.EnableLyearnAI]: enableLyearnAI } = useFeatureFlags();

  const onChangeText = useEvent((text: string) => {
    !disabled && onChange(text);
  });

  const EDITOR_CORE_PROPS = useMemo<NonNullable<AIBaseEditorProps['editorCoreProps']>>(
    () => ({
      variant: 'body-short-02',
      classes: {
        typography: '!min-h-[4.8rem] !max-h-fit',
        contentEditableContainer: enableLyearnAI ? 'me-56' : '',
      },
    }),
    [enableLyearnAI],
  );

  if (enableLyearnAI) {
    return (
      <AIBaseEditor
        FeedbackComponent={FeedbackComponent}
        SuggestionComponent={SuggestionComponent}
        className={cn(className, error ? nudgeBorderStyle : '')}
        classes={EDITOR_CLASSES}
        disabled={disabled}
        editorCoreProps={EDITOR_CORE_PROPS}
        editorOptionMap={AI_MESSAGE_INPUT_EDITOR_OPTION_MAP}
        editorOptions={AI_MESSAGE_INPUT_EDITOR_OPTIONS}
        placeholder={placeholder}
        textUseCase={textUseCase}
        topic={topic}
        value={value}
        onChangeText={onChangeText}
        onTopicError={onTopicError}
      />
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <Editor.Main
        enableMentions
        className={cn(className, EDITOR_CLASSES.root)}
        placeholder={placeholder}
        value={value}
        onChangeText={onChangeText}>
        <Editor.EditorCore
          className={cn('flex-1 overflow-y-auto overscroll-y-contain', EDITOR_CLASSES.editor)}
          {...EDITOR_CORE_PROPS}
        />
      </Editor.Main>
    </Suspense>
  );
}

export default memo(AIMessageInput);
